import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import store from '@/store'
import HomeView from '../views/HomeView.vue'
import { resetSeo } from '@/utils/seo'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/properties',
    name: 'properties',
    component: () => import('../views/PropertiesView.vue')
  },
  {
    path: '/property/:id',
    name: 'property-detail',
    component: () => import('../views/PropertyDetailView.vue'),
    props: true
  },
  {
    path: '/signin',
    name: 'signin',
    component: () => import('../views/SignInView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('../views/SignUpView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/ForgotPasswordView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/ResetPasswordView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('../views/ContactView.vue')
  },
  {
    path: '/blogs',
    name: 'blogs',
    component: () => import('../views/BlogsView.vue')
  },
  {
    path: '/blog/:id',
    name: 'blog-detail',
    component: () => import('../views/BlogDetailView.vue'),
    props: true
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('../views/AboutView.vue')
  },
  {
    path: '/agents/:id',
    name: 'agent-public-profile',
    component: () => import('../views/AgentProfileView.vue'),
    props: true
  },

  // ── Agent portal ─────────────────────────────────────────────────────────
  {
    path: '/agent',
    component: () => import('../layouts/AgentLayout.vue'),
    meta: { requiresAuth: true, role: 'agent' },
    children: [
      {
        path: 'dashboard',
        name: 'agent-dashboard',
        component: () => import('../views/agent/DashboardView.vue')
      },
      {
        path: 'properties',
        name: 'agent-properties',
        component: () => import('../views/agent/MyPropertiesView.vue')
      },
      {
        path: 'properties/add',
        name: 'agent-add-property',
        component: () => import('../views/agent/AddPropertyView.vue')
      },
      {
        path: 'properties/:id',
        name: 'agent-property-detail',
        component: () => import('../views/agent/PropertyDetailView.vue')
      },
      {
        path: 'properties/:id/edit',
        name: 'agent-edit-property',
        component: () => import('../views/agent/EditPropertyView.vue')
      },
      {
        path: 'profile',
        name: 'agent-profile',
        component: () => import('../views/agent/ProfileView.vue')
      },
      {
        path: 'inquiries',
        name: 'agent-inquiries',
        component: () => import('../views/agent/InquiriesView.vue')
      }
    ]
  },

  // ── Admin CRM ─────────────────────────────────────────────────────────────
  {
    path: '/admin',
    component: () => import('../layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, role: 'admin' },
    children: [
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('../views/admin/AdminDashboardView.vue')
      },
      {
        path: 'properties',
        name: 'admin-properties',
        component: () => import('../views/admin/AdminPropertiesView.vue')
      },
      {
        path: 'agents',
        name: 'admin-agents',
        component: () => import('../views/admin/AdminAgentsView.vue')
      },
      {
        path: 'inquiries',
        name: 'admin-inquiries',
        component: () => import('../views/admin/AdminInquiriesView.vue')
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('../views/admin/AdminSettingsView.vue')
      },
      {
        path: 'properties/create',
        name: 'admin-property-create',
        component: () => import('../views/admin/AdminPropertyCreateView.vue')
      },
      {
        path: 'properties/:id/edit',
        name: 'admin-property-edit',
        component: () => import('../views/admin/AdminPropertyEditView.vue')
      },
      {
        path: 'hero',
        name: 'admin-hero',
        component: () => import('../views/admin/AdminHeroView.vue')
      },
      {
        path: 'reviews',
        name: 'admin-reviews',
        component: () => import('../views/admin/AdminReviewsView.vue')
      },
      {
        path: 'blogs',
        name: 'admin-blogs',
        component: () => import('../views/admin/AdminBlogsView.vue')
      },
      {
        path: 'blogs/create',
        name: 'admin-blog-create',
        component: () => import('../views/admin/AdminBlogCreateView.vue')
      },
      {
        path: 'blogs/:id/edit',
        name: 'admin-blog-edit',
        component: () => import('../views/admin/AdminBlogEditView.vue')
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// ── Navigation guard ─────────────────────────────────────────────────────────
router.beforeEach((to, _from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const user = store.getters['auth/user']

  // Redirect logged-in users away from signin/signup
  if (to.meta.guestOnly && isAuthenticated) {
    const dest = user?.role === 'admin' ? '/admin/dashboard' : '/agent/dashboard'
    return next(dest)
  }

  // Protected routes
  if (to.meta.requiresAuth) {
    if (!isAuthenticated) {
      return next({ name: 'signin', query: { redirect: to.fullPath } })
    }

    // Role check — agent routes
    if (to.meta.role === 'agent') {
      if (user?.role !== 'agent') return next('/admin/dashboard')
      if (user?.status !== 'active') {
        store.dispatch('auth/logout')
        return next({ name: 'signin' })
      }
    }

    // Role check — admin routes
    if (to.meta.role === 'admin') {
      if (user?.role !== 'admin') return next('/agent/dashboard')
    }
  }

  next()
})

// Reset to default SEO on every navigation; individual pages override in mounted()
router.afterEach((to) => {
  if (!to.meta.requiresAuth) {
    resetSeo()
  }
})

export default router
